import { useCallback } from 'react';

import type { AddWipRecordFromScheduleRequestBodyDTO } from 'declarations/dto/wip';
import type { ApiErrorI } from 'declarations/interfaces/errors';
import type { NoopT } from 'declarations/types/common';
import { getErrorMessage, getValidationErrorMessage } from 'helpers/apiError';
import { useAddWipRecordFromSchedule } from 'hooks/api/useWip';
import { updateSnackbarNotification } from 'reduxStore/features/notifications/notificationsSlice';
import { useAppDispatch } from 'reduxStore/hooks';

import type { AddToWipFormValuesT } from '../validationSchema';

type UseAddToWipPopupParamsT = {
  repairOrderNumber: string;
  roofTopId: string;
  amount: number;
  onAddToWipSuccess?: () => void;
};

type UseAddToWipPopupReturnT = {
  isSubmitting: boolean;
  handleAddToWip: (data: AddToWipFormValuesT, onClose: NoopT) => void;
  handleClosePopup: (onClosePopupCb: NoopT) => NoopT;
};

const useAddToWipPopup = ({
  repairOrderNumber,
  roofTopId,
  amount,
  onAddToWipSuccess,
}: UseAddToWipPopupParamsT): UseAddToWipPopupReturnT => {
  const dispatch = useAppDispatch();

  const addWipMutation = useAddWipRecordFromSchedule({});

  const handleClosePopup = useCallback(
    (onClosePopupCb: NoopT) => () => {
      onClosePopupCb();
    },
    []
  );

  const handleAddToWip = useCallback(
    (data: AddToWipFormValuesT, onClose: NoopT) => {
      const body: AddWipRecordFromScheduleRequestBodyDTO = {
        repairOrderNumber,
        roofTopId,
        backlog: data.workItemType === 'backlog',
        repairOrderTotal: Number.isFinite(amount) ? amount : null,
      };

      addWipMutation.mutate(body, {
        onSuccess: () => {
          dispatch(
            updateSnackbarNotification({
              snackbarMessage: 'Repair order added to WIP',
              snackBarSeverity: 'success',
            })
          );
          onClose();
          onAddToWipSuccess?.();
        },
        onError: error => {
          const errorMsg =
            getErrorMessage(error as ApiErrorI) || getValidationErrorMessage(error as ApiErrorI);
          dispatch(
            updateSnackbarNotification({
              snackbarMessage: `Something went wrong. ${errorMsg || ''}`,
              snackBarSeverity: 'error',
            })
          );
          onClose();
        },
      });
    },
    [addWipMutation, amount, dispatch, onAddToWipSuccess, repairOrderNumber, roofTopId]
  );

  return {
    isSubmitting: addWipMutation.isLoading,
    handleAddToWip,
    handleClosePopup,
  };
};

export default useAddToWipPopup;
